import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bars3Icon } from "@heroicons/react/24/outline";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import classNames from "classnames";
import { LanguageSelector } from "@/components/LanguageSelector";
import { LanguageCard } from "@/components/LanguageCard";
import sendolaPayBusiness from "@/assets/Img/sendolapay-business-logo.png";
import sendolaLogo from "@/assets/Img/logo-sendola.png";
import HubspotContactForm from "@/components/HubspotContactForm";
import { Modal } from "@/components/Modal";

const menuItem =
  "flex w-full gap-2 items-center py-3 hover:text-primary-300 text-white cursor-pointer border-b-[1px] border-b-gray-600 last:border-b-0";
const navItem =
  "text-sm xl:text-base hover:text-gray-300 flex-shrink-0 text-white font-[500]";
const homeNavItem =
  "text-sm xl:text-base hover:text-primary-700 flex-shrink-0 text-black font-[500]";

export const CaboHeader = () => {
  const [t, i18n] = useTranslation("global");
  const [tCabo] = useTranslation("cabo");
  const currentLanguage = i18n.language;
  const pathname = window.location?.pathname;
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [menuTouched, setMenuTouched] = useState<boolean>(false);
  const [showLanguageBar, setShowLanguageBar] = useState<boolean>(false);
  const [verticalScrolled, setVerticalScrolled] = useState(false);
  const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] =
    useState<boolean>(false);
  const [loadingForm, setLoadingForm] = useState<boolean>(true);

  const ref = useOutsideClick(() => {
    if (menuOpen) setMenuOpen(false);
  });
  const isHome = useMemo(() => {
    if (pathname === "/") return true;
    return false;
  }, [pathname]);

  const navClassNames = useMemo(
    () => (isHome && !verticalScrolled ? homeNavItem : navItem),
    [isHome, verticalScrolled]
  );

  useEffect(() => {
    const checkVerticalScroll = () => {
      const scrollY = window.scrollY;
      setVerticalScrolled(scrollY > 60);
    };

    window.addEventListener("scroll", checkVerticalScroll);

    return () => {
      window.removeEventListener("scroll", checkVerticalScroll);
    };
  }, []);

  useEffect(() => {
    if (!currentLanguage) setShowLanguageBar(true);
  }, [currentLanguage]);

  return (
    <>
      <nav className="z-50 sticky top-0 transition-all duration-200 ease-in">
        <div
          className={classNames(
            "w-full flex justify-between items-center transition-all bg-dark-gray-900",
            {
              "h-16 px-4 py-2": true, // Mobile styles
              "lg:px-8 lg:h-20": true, // Desktop styles
            }
          )}
        >
          <div className="flex gap-4 md:gap-10 items-center flex-shrink-0">
            <a href="/">
              <img
                className="h-8 sm:h-9 md:h-10 xl:h-12 w-auto object-contain select-none"
                src={sendolaLogo}
                alt="SendolaPay Business Logo"
              />
            </a>

            <div className="hidden lg:flex h-full items-center gap-2 xs:gap-6">
              <a href="/cabo#individuals">
                <h1 className={navClassNames}>{tCabo("forIndividuals")}</h1>
              </a>
              <a href="/cabo#businesses">
                <h1 className={navClassNames}>{tCabo("forBusinesses")}</h1>
              </a>
            </div>
          </div>

          <div
            ref={ref}
            className="flex justify-end flex-shrink-0 lg:hidden w-fit ml-4"
          >
            <LanguageSelector
              blackText={!verticalScrolled && isHome}
              onClick={() => setShowLanguageBar(!showLanguageBar)}
            />
            <Bars3Icon
              className={classNames(
                "w-10 h-10 rounded cursor-pointer hover:bg-gray-900/30 p-2",
                {
                  "bg-gray-900": menuTouched,
                  "text-white": verticalScrolled || !isHome,
                  "text-black": !verticalScrolled && isHome,
                }
              )}
              onClick={() => setMenuOpen(!menuOpen)}
              onTouchStart={() => setMenuTouched(true)}
              onTouchEnd={() => setMenuTouched(false)}
            />
          </div>

          <LanguageSelector
            className="hidden lg:block"
            onClick={() => setShowLanguageBar(!showLanguageBar)}
          />
        </div>

        <div
          className={classNames(
            "block lg:hidden absolute overflow-hidden transition-all w-max min-w-60 rounded-b-lg flex-col bg-gradient-to-r from-sendolab-gray4 via-sendolab-gray2 to-sendolab-gray4 shadow-lg px-4 top-16 right-0 z-20 box-border",
            {
              "max-h-80": menuOpen,
              "max-h-0": !menuOpen,
            }
          )}
        >
          <a href="/cabo#individuals" className={menuItem}>
            <p className="text-sm lg:text-base">{tCabo("forIndividuals")}</p>
          </a>
          <a href="/cabo#businesses" className={menuItem}>
            <p className="text-sm lg:text-base">{tCabo("forBusinesses")}</p>
          </a>
        </div>
      </nav>

      <LanguageCard
        show={showLanguageBar}
        onClose={() => setShowLanguageBar(false)}
      />

      <Modal
        withClose
        open={isCreateAccountModalOpen}
        onClose={() => {
          setIsCreateAccountModalOpen(false);
          setLoadingForm(true);
        }}
      >
        <div className="w-full flex flex-col items-center">
          <div className="w-full pb-8 flex justify-between items-center">
            <img
              className="h-9 sm:h-10 md:h-12 xl:h-14 w-auto object-contain select-none"
              src={sendolaPayBusiness}
              alt="SendolaPay Business Logo"
            />
          </div>
          {loadingForm && (
            <p className="w-full text-center text-black font-semibold">
              {t("Loading")}...
            </p>
          )}
          <HubspotContactForm
            region="na1"
            portalId="44207752"
            formId="db94a133-c8f4-47bd-9c41-fdefdc87209a"
            onReady={() => {
              setLoadingForm(false);
            }}
          />
        </div>
      </Modal>
    </>
  );
};
